import React, { FC, ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { NavigationLessAppPage } from "./NavigationLessAppPage";
import { useTranslation } from "react-i18next";
import {
  QueryObserverSuccessResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { AppInfo } from "shared-ts-app-info";

export interface BootLoadStep<T> {
  message: string;
  queryResult: UseQueryResult<T>;
}

export interface BootingPageProps<
  TQueries extends Record<string, UseQueryResult<any>>
> {
  appInfo: AppInfo;
  queries: TQueries;
  children: (results: {
    [K in keyof TQueries]: QueryObserverSuccessResult<
      TQueries[K] extends UseQueryResult<infer TData, any> ? TData : never,
      Error
    >;
  }) => ReactNode;
}

export const BootingPage = <
  TQueries extends Record<string, UseQueryResult<any, Error>>
>(
  props: BootingPageProps<TQueries>
) => {
  const { t } = useTranslation("shared-ts-ui-app-boot");
  const queryResults = Object.values(props.queries);
  const allSuccess = queryResults.every((query) => query.isSuccess);
  const firstError = queryResults.find((it) => it.isError);

  if (allSuccess) {
    const successResults = props.queries as {
      [K in keyof TQueries]: QueryObserverSuccessResult<
        TQueries[K] extends UseQueryResult<infer TData, any> ? TData : never,
        Error
      >;
    };

    return props.children(successResults);
  }

  return (
    <NavigationLessAppPage appInfo={props.appInfo}>
      <Typography>{t("Booting")}</Typography>
      {firstError && <Box sx={{ pl: 1 }}>{firstError.error?.name}</Box>}
    </NavigationLessAppPage>
  );
};
